import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import Button from '../_Core/Button';
import redux, { IState, ReduxMap } from '../../redux/redux';
import { FormatDate } from '../../utils/Tools';

interface IPayoutState {
  FairName: string;
  Entity: {
    Id: number;
    BusinessName: string;
    PersonName: string;
    Tin: string;
    HasTin: boolean;
    Attn: string;
    Address1: string;
    Address2: string;
    Address3: string;
    City: string;
    State: string;
    Zip: string;
  };
  Pending: { Total: number; Fees: number; Balance: number; ClientPaid?: string } | null;
  CanRequestPayout: boolean;
  States: { Abbreviation: string; Name: string }[];
  Payouts: {
    Id: number;
    Processed?: string | null;
    RemovedAtUtc?: string | null;
    CheckAmount: number;
    Notes?: string;
    DeductPendingInvoices: boolean;
  }[];
}

const money = (n: number) =>
  (n ?? 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

const PayoutPage = (props: IState) => {
  const [data, setData] = useState<IPayoutState | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    BusinessName: '',
    PersonName: '',
    Tin: '',
    Attn: '',
    Address1: '',
    Address2: '',
    Address3: '',
    City: '',
    State: '-1',
    Zip: '',
  });
  const [deductPay, setDeductPay] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    store.server
      .postApi<ServerResponse<IPayoutState>>('../Payout/Data', {})
      .then((x) => {
        if (x.Success) {
          setData(x.Value);
          const e = x.Value.Entity;
          setForm({
            BusinessName: e.BusinessName || '',
            PersonName: e.PersonName || '',
            Tin: e.Tin || '',
            Attn: e.Attn || '',
            Address1: e.Address1 || '',
            Address2: e.Address2 || '',
            Address3: e.Address3 || '',
            City: e.City || '',
            State: e.State || '-1',
            Zip: e.Zip || '',
          });
        } else {
          if (x.LoginRequired) redux.showLogin(true);
          toast.error(x.Message || 'Unable to load payout information.', { autoClose: false });
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (props.User) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.User]);

  const save = () => {
    setSaving(true);
    store.server
      .postApi<ServerResponse<IPayoutState>>('../Payout/SaveDetailsJson', {
        BusinessName: form.BusinessName,
        PersonName: form.PersonName,
        Tin: form.Tin,
        Attn: form.Attn,
        Address1: form.Address1,
        Address2: form.Address2,
        Address3: form.Address3,
        City: form.City,
        State: form.State,
        Zip: form.Zip,
      })
      .then((x) => {
        if (x.Success) {
          setData(x.Value);
          toast.success('Saved.');
          const e = x.Value.Entity;
          setForm((f) => ({ ...f, Tin: e.Tin || f.Tin }));
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      })
      .finally(() => setSaving(false));
  };

  const requestPayout = () => {
    setSaving(true);
    store.server
      .postApi<ServerResponse<IPayoutState>>(`../Payout/RequestPayoutJson?pay=${deductPay}`, {})
      .then((x) => {
        if (x.Success) {
          setData(x.Value);
          toast.success('Payout requested.');
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      })
      .finally(() => setSaving(false));
  };

  if (loading || !data) {
    return (
      <div className="main-body-page grow" data-testid="payout-page">
        <h2>Payout Request</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="main-body-page grow" data-testid="payout-page">
      <h2>Payout Request</h2>
      <div className="alert alert-info">
        If we collect credit card payment on your behalf using our merchant account; before we can release those funds we must know how these funds should be reported to the IRS.
      </div>

      {!data.Pending && (
        <div className="alert alert-warning" data-testid="payout-no-pending">
          <h4>
            <i className="fa fa-exclamation-triangle" /> No Pending Payouts
          </h4>
          At this time you do not have any pending payouts. Payouts only apply to contests that have us collect credit card payments on their behalf.
        </div>
      )}

      {data.Pending && !data.Entity.HasTin && (
        <div className="alert alert-warning" data-testid="payout-tax-missing">
          <h4>
            <i className="fa fa-exclamation-triangle" /> Tax Info Missing
          </h4>
          We are missing the required tax information to process any pending payments.
        </div>
      )}

      <div className="row">
        <div className="col-sm-7">
          <h4>Payee Tax Information</h4>
          <div className="form-horizontal">
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="payout-info-name-business">
                Organization Name (Pay To)
              </label>
              <div className="col-sm-8">
                <input
                  id="payout-info-name-business"
                  data-testid="payout-business-name"
                  className="form-control"
                  value={form.BusinessName}
                  onChange={(e) => setForm({ ...form, BusinessName: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="payout-info-name-person">
                Tax Payer Name
              </label>
              <div className="col-sm-8">
                <input
                  id="payout-info-name-person"
                  data-testid="payout-person-name"
                  className="form-control"
                  value={form.PersonName}
                  onChange={(e) => setForm({ ...form, PersonName: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="payout-info-tin">
                Tax Identification Number (SSN)
              </label>
              <div className="col-sm-8">
                <input
                  id="payout-info-tin"
                  data-testid="payout-tin"
                  className="form-control"
                  value={form.Tin}
                  onChange={(e) => setForm({ ...form, Tin: e.target.value })}
                />
              </div>
            </div>

            <h3>Mailing Address</h3>
            <p>Please tell us where the check should be sent.</p>

            {[
              ['Attn', 'Attn', 'payout-attn'],
              ['Address1', 'Address', 'payout-address1'],
              ['Address2', 'Address 2', 'payout-address2'],
              ['Address3', 'Address 3', 'payout-address3'],
              ['City', 'City', 'payout-city'],
              ['Zip', 'Zip', 'payout-zip'],
            ].map(([key, label, testId]) => (
              <div className="form-group" key={key}>
                <label className="control-label col-sm-4">{label}</label>
                <div className="col-sm-8">
                  <input
                    className="form-control"
                    data-testid={testId}
                    value={(form as any)[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  />
                </div>
              </div>
            ))}

            <div className="form-group">
              <label className="control-label col-sm-4">State</label>
              <div className="col-sm-8">
                <select
                  className="form-control"
                  data-testid="payout-state"
                  value={form.State}
                  onChange={(e) => setForm({ ...form, State: e.target.value })}
                >
                  <option value="-1">select...</option>
                  {data.States.map((s) => (
                    <option key={s.Abbreviation} value={s.Abbreviation}>
                      {s.Name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <div className="col-sm-8 col-sm-offset-4">
                <Button type="save" text={saving ? 'Saving...' : 'Save'} onClick={save} disabled={saving} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-sm-5">
          <h4>Pending Payout</h4>
          <div className="alert alert-info">
            <h4>Payout Request Instructions</h4>
            <p>
              Payout can be made once event has completed and you have provided all the required payout and tax information. Payments are processed about 2 to 6 weeks after the end of your contest to
              account for any charge backs or refunds.
            </p>
            <p>
              If you are needing your payout sooner, or are having trouble please reach out to us at <a href="mailto:support@zFairs.com">support@zfairs.com</a>.
            </p>
          </div>

          {data.Pending && !data.Pending.ClientPaid && (
            <div data-testid="payout-pending-amounts">
              <span className="label label-info">*These are estimates*</span>
              <br />
              <strong>Total:</strong> {money(data.Pending.Total)}
              <br />
              <strong>Fees:</strong> {money(data.Pending.Fees)}
              <br />
              <strong>Balance:</strong> {money(data.Pending.Balance)}
              <br />
              <br />
              {data.CanRequestPayout && (
                <div className="soft-border text-center">
                  <input type="checkbox" id="deductPay" data-testid="payout-deduct" checked={deductPay} onChange={(e) => setDeductPay(e.target.checked)} />{' '}
                  <label htmlFor="deductPay">Deduct any outstanding invoices or subscription fees.</label>
                  <br />
                  <button type="button" className="btn btn-warning" data-testid="payout-request-btn" disabled={saving} onClick={requestPayout}>
                    Request Payout
                  </button>
                </div>
              )}
            </div>
          )}

          {data.Payouts.map((p) => (
            <div key={p.Id} data-testid={`payout-history-${p.Id}`}>
              {p.Processed && !p.RemovedAtUtc && (
                <div className="alert alert-success">
                  Payout {p.Id} at {FormatDate(p.Processed)} for {money(p.CheckAmount)}
                </div>
              )}
              {p.Processed && p.RemovedAtUtc && (
                <div className="alert alert-danger">
                  Payout {p.Id} was canceled. Notes: {p.Notes}
                </div>
              )}
              {!p.Processed && !p.RemovedAtUtc && <div className="alert alert-info">Payout has been requested, Payout Id: {p.Id}</div>}
              {p.DeductPendingInvoices && (
                <div className="alert alert-success">It was selected to deduct any outstanding invoices or subscription fees from this payout. Payout Id: {p.Id}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReduxMap(PayoutPage);
